import { useCallback } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { CircleIconWrapper } from '@ContractBuilder/contract-builder-header/components/CircleIconWrapper';
import { ViewPath } from '@ContractBuilder/contract-builder-header/constants';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { useEntityStore } from '@ContractBuilder/store';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import { useRenewalSummaryStatus } from '@features/renewal-summary';
import { getDocumentMode, isSubmission } from '@helpers/getDocumentMode';
import { useDocumentLocation } from '@hooks/useDocumentLocation';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import Nav from '@src/routes';
import { cn } from 'cn';
import { FileDiff } from 'lucide-react';

/**
 * Router state asking the Compare View to open the Summary of Changes drawer
 * once it has mounted. Opening it before navigating does not stick: the
 * comments and validation sections close any drawer when the path changes.
 */
export const OPEN_RENEWAL_SUMMARY_STATE = { openDrawer: DrawerLayoutComponent.RenewalSummary } as const;

export const isOpenRenewalSummaryState = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  (state as { openDrawer?: unknown }).openDrawer === DrawerLayoutComponent.RenewalSummary;

/**
 * Summary of Changes drawer toggle, on a renewal's Compare View. The status
 * hook runs on every view of the renewal, so the "Summary ready" toast still
 * arrives on the Form or Contract View. Shown only to callers on the tenant's
 * `renewal_summary` preview allowlist (GET /feature_access); the routes
 * enforce the same list server-side.
 */
export const ShowRenewalSummarySection = () => {
  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const { closeDrawer, isDrawerOpen, openDrawer, state } = useDrawerStore(
    ({ closeDrawer, isOpen, openDrawer, state }) => ({
      closeDrawer,
      isDrawerOpen: isOpen,
      openDrawer,
      state,
    }),
  );
  const submission = useEntityStore(({ submission }) => submission);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isBlockComparisonView } = useDocumentLocation();
  const getFeatureFlagState = useFeatureFlags();
  const { renewalSummary: hasPreviewAccess } = useFeatureAccess();

  // Key on the id in the URL and wait until the store agrees (see ShowPbqaEvalSection).
  const routeSubmissionId = getDocumentMode(pathname).ids[0];
  const isStoreCurrent = Boolean(routeSubmissionId) && submission?.id === routeSubmissionId;
  const isRenewal = Boolean(submission?.renewalParentId);
  const isEnabled =
    getFeatureFlagState('RenewalSummary') && hasPreviewAccess && isSubmission(pathname) && isRenewal && isStoreCurrent;

  const isPanelOpen = isDrawerOpen && state?.type === DrawerLayoutComponent.RenewalSummary;
  const isEditing = editingBlockId !== undefined;

  const openPanel = useCallback(
    () => openDrawer({ type: DrawerLayoutComponent.RenewalSummary, props: {} }),
    [openDrawer],
  );

  const handleViewSummary = useCallback(() => {
    if (isBlockComparisonView) return openPanel();
    if (!routeSubmissionId) return undefined;
    return navigate(generatePath(Nav.Submission, { id: routeSubmissionId, '*': ViewPath.ComparisonView }), {
      state: OPEN_RENEWAL_SUMMARY_STATE,
    });
  }, [isBlockComparisonView, navigate, openPanel, routeSubmissionId]);

  useRenewalSummaryStatus({
    renewalId: routeSubmissionId ?? '',
    renewalName: submission?.name,
    enabled: isEnabled,
    isPanelOpen,
    onViewSummary: handleViewSummary,
  });

  const handleToggleClick = () => (isPanelOpen ? closeDrawer() : openPanel());

  return (
    <Show when={isEnabled && isBlockComparisonView}>
      <Tooltip content="Summary of Changes" disabled={isEditing}>
        <CircleIconWrapper
          data-testid="renewal-summary-toggle-button"
          icon={FileDiff}
          isActive={isPanelOpen}
          onClick={handleToggleClick}
          wrapperClassName={cn(isEditing && 'pointer-events-none bg-transparent opacity-50')}
        />
      </Tooltip>
    </Show>
  );
};
